import SearchIcon from "@mui/icons-material/Search";
import {
	Grid2,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	GridReadyEvent,
	IRowNode,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "../../../../account";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { emailTopicsUrl } from "../../../../configurationMenu";
import { useContextMenuMESF } from "../../../../controls/contextMenu";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "../../../../controls/snackbars/notification-snackbar";
import { themeDXT } from "../../../../css/themeMESF";
import { useEmailTopics } from "../EmailTopicsPage";
import type { EmailTopic } from "../models/EmailTopic";
import { setEmailTopicEnable } from "../repositories/EmailTopicsRepository";
import { RecipientTypeLegend } from "./RecipientTypeChips";
import { UpsertDeleteModal } from "./UpsertDeleteModal";
import { useEmailTopicsOptionsFunctions } from "./customHooks/useEmailTopicsOptionsFunctions";
import { useTableEmailTopics } from "./customHooks/useTableEmailTopics";

type Props = {
	emailTopicId?: string;
	routeModal: "" | "create" | "update";
};

export const TableEmailTopics = ({ emailTopicId, routeModal }: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { getUsername } = useToken();
	const username = getUsername();
	const includeSystem = username === "admin" || username === "dexteel";

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [showModal, setShowModal] = useState<"" | "create" | "update">("");
	const [selectedRowData, setSelectedRowData] = useState<EmailTopic | null>(
		null,
	);
	const [topicToEnable, setTopicToEnable] = useState<EmailTopic | null>(null);
	const [message, setMessage] = useState("");
	const [error, setError] = useState("");

	const {
		data,
		isLoading,
		isError,
		error: queryError,
	} = useEmailTopics({ includeSystem });

	const getAssignedAddresses = () => {
		if (!selectedRowData) {
			return [];
		}

		return (data?.addresses ?? []).filter(
			(address) => address.EmailTopicId === selectedRowData.EmailTopicId,
		);
	};

	const selectEmailTopicNode = (topic: EmailTopic) => {
		const node = gridApi?.getRowNode(String(topic.EmailTopicId));

		if (node) {
			gridApi?.ensureNodeVisible(node);
			gridApi?.deselectAll();
			node.setSelected(true);
		}
	};

	const editEmailTopic = (topic: EmailTopic, shouldNavigate = true) => {
		selectEmailTopicNode(topic);
		setSelectedRowData(topic);
		setShowModal("update");

		if (shouldNavigate) {
			navigate(`${emailTopicsUrl}/${topic.EmailTopicId}`);
		}
	};

	const enableTopicMutation = useMutation({
		mutationFn: setEmailTopicEnable,
		onSuccess: async (_result, { enabled }) => {
			await queryClient.invalidateQueries({ queryKey: ["emailTopics"] });
			setMessage(
				`The email topic was ${enabled ? "enabled" : "disabled"} succesfully`,
			);
		},
		onError: (mutationError: Error) => {
			setError(mutationError.message);
		},
	});

	const handleEnableEmailTopic = (topic: EmailTopic) => {
		selectEmailTopicNode(topic);
		setTopicToEnable(topic);
	};

	const onEnableConfirmationClose = (value?: string) => {
		const topic = topicToEnable;

		setTopicToEnable(null);
		gridApi?.deselectAll();

		if (value === "OK" && topic) {
			enableTopicMutation.mutate({
				emailTopicId: topic.EmailTopicId,
				enabled: !topic.Enabled,
			});
		}
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useEmailTopicsOptionsFunctions({
		editEmailTopic,
	});

	const { columnDefs } = useTableEmailTopics({
		editEmailTopic,
		enableEmailTopic: handleEnableEmailTopic,
		showContextMenu,
	});

	const defaultColDef: ColDef = {
		flex: 1,
		filter: false,
		floatingFilter: false,
		sortable: true,
		editable: false,
		resizable: false,
		wrapText: false,
		autoHeight: false,
		wrapHeaderText: true,
		suppressHeaderMenuButton: true,
		cellStyle: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	};

	const handleRowDoubleClicked = (event: RowDoubleClickedEvent<EmailTopic>) => {
		if (!event.data) {
			return;
		}

		editEmailTopic(event.data);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const contextData = params.node?.data as EmailTopic | undefined;
			const nativeEvent = (
				params as GetContextMenuItemsParams & { event?: MouseEvent }
			).event;

			if (contextData && nativeEvent) {
				params.api.deselectAll();
				params.node?.setSelected(true);
				showContextMenu(
					nativeEvent as unknown as React.MouseEvent,
					contextData,
					"emailTopicContext",
				);
			}

			return [];
		},
		[showContextMenu],
	);

	const onModalHide = () => {
		gridApi?.deselectAll();
		setSelectedRowData(null);
		setShowModal("");
		navigate(emailTopicsUrl);
	};

	useEffect(() => {
		registerConfig({
			id: "emailTopicContext",
			getOptions: getMenuOptions,
		});
	}, [registerConfig]);

	useEffect(() => {
		if (isError && queryError) {
			setError(queryError.message);
		}
	}, [isError, queryError]);

	useEffect(() => {
		if (isLoading) {
			return;
		}

		if (routeModal === "") {
			setSelectedRowData(null);
			setShowModal("");
			gridApi?.deselectAll();
			return;
		}

		if (!emailTopicId || !gridApi) {
			return;
		}

		const node: IRowNode | undefined = gridApi?.getRowNode(emailTopicId);

		if (node) {
			gridApi?.ensureNodeVisible(node);
			gridApi?.deselectAll();
			node.setSelected(true);
			setSelectedRowData(node.data as EmailTopic);
			setShowModal(routeModal);
			return;
		}

		onModalHide();
	}, [emailTopicId, gridApi, isLoading, routeModal, data?.topics]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Email Topics
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextField
						fullWidth
						label="Search"
						variant="outlined"
						size="small"
						margin="dense"
						value={filterValue}
						onChange={(event) => {
							setFilterValue(event.target.value);
							gridApi?.setGridOption("quickFilterText", event.target.value);
						}}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" type="submit">
											<SearchIcon />
										</IconButton>
									</InputAdornment>
								),
							} as any,
						}}
					/>
				</Grid2>
				<Grid2
					container
					justifyContent="center"
					alignItems="center"
					sx={{ width: "100%" }}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper
							elevation={2}
							sx={{
								height: "73vh",
								width: "100%",
								"& .ag-status-bar": { minHeight: 36, alignItems: "center" },
							}}
						>
							<AgGridReact
								loading={isLoading}
								rowData={data?.topics ?? []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={handleRowDoubleClicked}
								onGridReady={(params: GridReadyEvent<EmailTopic>) => {
									setGridApi(params.api);
								}}
								getRowId={(params) => String(params.data.EmailTopicId)}
								loadingOverlayComponent={CenteredLazyLoading}
								gridOptions={{
									theme: themeDXT,
									rowSelection: {
										mode: "singleRow" as const,
										checkboxes: false,
										enableClickSelection: true,
									},
									statusBar: {
										statusPanels: [
											{
												statusPanel: RecipientTypeLegend,
												align: "left",
											},
											{
												statusPanel: "agTotalRowCountComponent",
												align: "right",
											},
										],
									},
								}}
							/>
						</Paper>
					</Grid2>
				</Grid2>
			</Grid2>
			<UpsertDeleteModal
				modal={showModal}
				onHide={onModalHide}
				topic={selectedRowData}
				assignedAddresses={getAssignedAddresses()}
				includeSystem={includeSystem}
			/>
			<ConfirmationDialogRaw
				open={topicToEnable !== null}
				onClose={onEnableConfirmationClose}
				title={`${topicToEnable?.Enabled ? "Disable" : "Enable"} Confirmation`}
				message={
					<>
						You are about to {topicToEnable?.Enabled ? "disable" : "enable"} the
						email topic <b>{topicToEnable?.EmailTopicName}</b>. Are you sure you
						want to proceed?
					</>
				}
				suffixTitle="Email Topics"
			/>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
